/**
 * Wejście partnera na konto najemcy — obie strony jednej zgody.
 *
 *   • `useKartaPomocy`   — strona NAJEMCY: kto go obsługuje, kontakt, wydanie
 *     i odwołanie sześciocyfrowego kodu, historia wejść na jego konto.
 *   • `useSesjeWsparcia` — lista sesji (najemca widzi swoje, partner swoje,
 *     super-admin wszystkie) wraz z dziennikiem pojedynczej sesji.
 *   • `useWejscieWsparcia` — strona PARTNERA: podanie kodu i otwarcie sesji.
 *
 * Cyfry kodu wracają z bazy DOKŁADNIE RAZ (w bazie leży tylko skrót), więc
 * `code` z `wygenerujKod()` jest jedyną chwilą, w której da się je pokazać —
 * po przeładowaniu ekranu zostaje sam termin ważności.
 */

import type {
  SupportAccessEvent,
  SupportAccessGrant,
  SupportAccessSession,
  SupportHelpCard,
} from '@insolitum/types';
import { useCallback, useEffect, useState } from 'react';

import { supabase } from '../lib/supabase';
import { zapamietajSesje, type AktywnaSesjaWsparcia } from '../lib/supportSession';

// ─────────────────────────────────────────────────────────────────────────────
// NAJEMCA: karta pomocy + zgoda
// ─────────────────────────────────────────────────────────────────────────────

export interface UseKartaPomocy {
  karta: SupportHelpCard | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Zwraca kod — jedyny moment, w którym cyfry są widoczne.
   *
   * Zakresu nie da się tu podać: baza przyjmuje wyłącznie pełny, bo „tylko
   * podgląd" nie ma egzekucji w politykach zapisu (migracja 462). Gdy zakres
   * zacznie być pilnowany, wróci tu jako parametr — do tego czasu jego brak
   * jest jedyną uczciwą wersją tego API.
   */
  wygenerujKod: (opts?: { minutes?: number; purpose?: string }) => Promise<SupportAccessGrant>;
  odwolajKod: (grantId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useKartaPomocy(organizationId: string | null): UseKartaPomocy {
  const [karta, setKarta] = useState<SupportHelpCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchKarta = useCallback(async () => {
    if (!organizationId) {
      setKarta(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase.rpc('support_access_karta_pomocy', {
        p_organization_id: organizationId,
      });
      if (rpcError) throw new Error(rpcError.message);
      setKarta(((data as SupportHelpCard[]) ?? [])[0] ?? null);
    } catch (err) {
      console.error('Błąd karty pomocy:', err);
      setError(err instanceof Error ? err.message : 'Nie udało się pobrać danych opiekuna');
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchKarta();
  }, [fetchKarta]);

  const wygenerujKod = useCallback<UseKartaPomocy['wygenerujKod']>(
    async (opts) => {
      if (!organizationId) throw new Error('Brak organizacji');
      const { data, error: rpcError } = await supabase.rpc('support_access_kod_wygeneruj', {
        p_organization_id: organizationId,
        p_partner_org_id: null,
        p_purpose: opts?.purpose ?? null,
        p_minutes: opts?.minutes ?? 30,
        p_scope: 'pelny',
      });
      if (rpcError) throw new Error(rpcError.message);
      const grant = ((data as SupportAccessGrant[]) ?? [])[0];
      if (!grant?.code) throw new Error('Baza nie zwróciła kodu');
      await fetchKarta();
      return grant;
    },
    [organizationId, fetchKarta]
  );

  const odwolajKod = useCallback(
    async (grantId: string) => {
      const { error: rpcError } = await supabase.rpc('support_access_kod_odwolaj', {
        p_grant_id: grantId,
      });
      if (rpcError) throw new Error(rpcError.message);
      await fetchKarta();
    },
    [fetchKarta]
  );

  return { karta, isLoading, error, wygenerujKod, odwolajKod, refetch: fetchKarta };
}

// ─────────────────────────────────────────────────────────────────────────────
// Sesje i dziennik — ten sam hook dla najemcy, partnera i super-admina.
// Zasięg rozstrzyga funkcja w bazie, nie ten kod.
// ─────────────────────────────────────────────────────────────────────────────

export interface UseSesjeWsparcia {
  sesje: SupportAccessSession[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  pobierzDziennik: (sessionId: string) => Promise<SupportAccessEvent[]>;
  zakonczSesje: (sessionId: string, reason?: 'partner' | 'najemca' | 'admin') => Promise<void>;
}

export function useSesjeWsparcia(organizationId?: string | null, limit = 50): UseSesjeWsparcia {
  const [sesje, setSesje] = useState<SupportAccessSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSesje = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase.rpc('support_access_sesje', {
        p_organization_id: organizationId ?? null,
        p_limit: limit,
      });
      if (rpcError) throw new Error(rpcError.message);
      setSesje((data as SupportAccessSession[]) ?? []);
    } catch (err) {
      console.error('Błąd listy sesji wsparcia:', err);
      setError(err instanceof Error ? err.message : 'Nie udało się pobrać sesji wsparcia');
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, limit]);

  useEffect(() => {
    fetchSesje();
  }, [fetchSesje]);

  const pobierzDziennik = useCallback(async (sessionId: string) => {
    const { data, error: rpcError } = await supabase.rpc('support_access_dziennik', {
      p_session_id: sessionId,
      p_limit: 1000,
    });
    if (rpcError) throw new Error(rpcError.message);
    return (data as SupportAccessEvent[]) ?? [];
  }, []);

  const zakonczSesjeById = useCallback(
    async (sessionId: string, reason: 'partner' | 'najemca' | 'admin' = 'najemca') => {
      const { error: rpcError } = await supabase.rpc('support_access_zakoncz', {
        p_session_id: sessionId,
        p_reason: reason,
      });
      if (rpcError) throw new Error(rpcError.message);
      await fetchSesje();
    },
    [fetchSesje]
  );

  return {
    sesje,
    isLoading,
    error,
    refetch: fetchSesje,
    pobierzDziennik,
    zakonczSesje: zakonczSesjeById,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// PARTNER: wykupienie kodu
// ─────────────────────────────────────────────────────────────────────────────

export interface UseWejscieWsparcia {
  wchodzi: boolean;
  error: string | null;
  /**
   * `partnerOrgId` mówi bazie, W CZYIM IMIENIU wchodzimy — bez tego tryb
   * podglądu („Przeglądasz panel jako partner”) odbijał się o komunikat
   * „dostepne wylacznie dla aktywnego partnera”, bo `auth.uid()` to wtedy
   * super-admin, a super-admin nie ma wiersza w `partners` (migracja 469).
   * Dla prawdziwego partnera wartość jest równa jego własnej organizacji,
   * a podanie cudzej baza odrzuca — więc przekazujemy ją zawsze.
   */
  wejdz: (kod: string, partnerOrgId?: string | null) => Promise<AktywnaSesjaWsparcia>;
}

export function useWejscieWsparcia(): UseWejscieWsparcia {
  const [wchodzi, setWchodzi] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wejdz = useCallback(async (kod: string, partnerOrgId?: string | null) => {
    setWchodzi(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('support_access_kod_uzyj', {
        p_code: kod,
        p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        p_partner_org_id: partnerOrgId ?? null,
      });
      if (rpcError) throw new Error(rpcError.message);

      // Pusty zbiór to nietrafiony kod — funkcja CELOWO nie rzuca wyjątku,
      // bo wyjątek cofnąłby licznik prób razem z podtransakcją (migracja 459).
      const wiersz = ((data as Array<Record<string, unknown>>) ?? [])[0];
      if (!wiersz) {
        throw new Error('Kod nieprawidłowy, zużyty lub wygasły');
      }

      const sesja: AktywnaSesjaWsparcia = {
        sessionId: String(wiersz.session_id),
        organizationId: String(wiersz.organization_id),
        organizationName: String(wiersz.organization_name ?? ''),
        organizationSlug: String(wiersz.organization_slug ?? ''),
        scope: (wiersz.scope as AktywnaSesjaWsparcia['scope']) ?? 'pelny',
        expiresAt: String(wiersz.expires_at),
      };
      zapamietajSesje(sesja);
      return sesja;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Nie udało się wejść na konto klienta';
      setError(msg);
      throw new Error(msg);
    } finally {
      setWchodzi(false);
    }
  }, []);

  return { wchodzi, error, wejdz };
}

// ─────────────────────────────────────────────────────────────────────────────
// SUPER-ADMIN: wejście serwisowe bez kodu (migracja 837)
// ─────────────────────────────────────────────────────────────────────────────

export interface UseWejscieSerwisowe {
  wchodzi: boolean;
  /**
   * Otwiera sesję serwisową w organizacji klienta. Powód (10–500 znaków)
   * klient zobaczy w historii wejść na swoje konto; czas 15–240 minut
   * pilnuje baza. Druga próba w trakcie trwającej sesji oddaje tę samą sesję.
   */
  wejdz: (organizationId: string, powod: string, minuty: number) => Promise<AktywnaSesjaWsparcia>;
}

export function useWejscieSerwisowe(): UseWejscieSerwisowe {
  const [wchodzi, setWchodzi] = useState(false);

  const wejdz = useCallback(async (organizationId: string, powod: string, minuty: number) => {
    setWchodzi(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('superadmin_wejscie_serwisowe', {
        p_organization_id: organizationId,
        p_powod: powod,
        p_minuty: minuty,
        p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      if (rpcError) throw new Error(rpcError.message);
      const wiersz = ((data as Array<Record<string, unknown>>) ?? [])[0];
      if (!wiersz) throw new Error('Baza nie otworzyła sesji serwisowej');

      const sesja: AktywnaSesjaWsparcia = {
        sessionId: String(wiersz.session_id),
        organizationId: String(wiersz.organization_id),
        organizationName: String(wiersz.organization_name ?? ''),
        organizationSlug: String(wiersz.organization_slug ?? ''),
        scope: (wiersz.scope as AktywnaSesjaWsparcia['scope']) ?? 'pelny',
        expiresAt: String(wiersz.expires_at),
        rodzaj: 'serwis',
      };
      zapamietajSesje(sesja);
      return sesja;
    } finally {
      setWchodzi(false);
    }
  }, []);

  return { wchodzi, wejdz };
}
